import { useQuery } from '@apollo/client';

import { GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS } from 'lib/api/queries';
import {
  AVAILABLE_MATERIALS_KEY,
  PL_CONSUMPTIONS_KEY,
} from 'lib/selectors/getPickingFieldConfig';

/**
 * Reads the WOO's available materials from customFields[AVAILABLE_MATERIALS_KEY],
 * written by the dynamics365-connector's available-materials sync (see
 * getPickingFieldConfig for the shape).
 *
 * cache-and-network so cached materials render immediately while a fresh read
 * picks up any connector sync since the last visit; useSubmitPicks does its own
 * network-only read before writing, so a stale cache can never corrupt a submit.
 *
 * Returns { materials, plConsumptions, loading, loaded, error, refetch } where materials is the
 * item-number map or null when the WOO has no synced materials (picking stays
 * blocked), and plConsumptions is the WOO's durable pick/consumption entries
 * (empty array when none) — the record usePickingSummary rebuilds submitted
 * pick rows from after a reload. `loaded` is true once the read has returned data;
 * `loading` stays true during background refreshes of data already loaded.
 */
const useAvailableMaterials = (wooRef) => {
  const { data, loading, error, refetch } = useQuery(
    GET_WORK_ORDER_OPERATION_CUSTOM_FIELDS,
    {
      variables: { workOrderOperationRef: wooRef },
      skip: !wooRef,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  const customFields = data?.erpWorkOrderOperation?.customFields;
  const materials = customFields?.[AVAILABLE_MATERIALS_KEY] || null;
  const plConsumptions = Array.isArray(customFields?.[PL_CONSUMPTIONS_KEY])
    ? customFields[PL_CONSUMPTIONS_KEY]
    : [];

  return {
    materials,
    plConsumptions,
    loading,
    loaded: data !== undefined,
    error,
    refetch,
  };
};

export default useAvailableMaterials;
